import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

const Login: React.FC = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [pendingConfirmation, setPendingConfirmation] = useState(false)
  const pollTimerRef = useRef<number | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) {
        window.clearTimeout(pollTimerRef.current)
      }
    }
  }, [])

  const finalizeLogin = (data: any) => {
    localStorage.setItem('token', data.token)
    localStorage.setItem('user', JSON.stringify(data.user))
    navigate('/dashboard')
  }

  const pollLoginStatus = async (challengeId: string) => {
    try {
      const statusResponse = await fetch(`/api/auth/login-status/${encodeURIComponent(challengeId)}`)
      const statusData = await statusResponse.json()

      if (!statusResponse.ok) {
        setPendingConfirmation(false)
        setLoading(false)
        setError(statusData.error || 'Не удалось проверить статус подтверждения')
        return
      }

      if (statusData.status === 'APPROVED' && statusData.token && statusData.user) {
        setPendingConfirmation(false)
        setLoading(false)
        finalizeLogin(statusData)
        return
      }

      if (statusData.status === 'REJECTED') {
        setPendingConfirmation(false)
        setLoading(false)
        setError('Вход отклонен в Telegram')
        return
      }

      if (statusData.status === 'EXPIRED') {
        setPendingConfirmation(false)
        setLoading(false)
        setError('Время подтверждения истекло. Войдите снова.')
        return
      }

      pollTimerRef.current = window.setTimeout(() => {
        pollLoginStatus(challengeId)
      }, 2000)
    } catch (err) {
      setPendingConfirmation(false)
      setLoading(false)
      setError('Ошибка проверки подтверждения в Telegram')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setPendingConfirmation(false)

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      })

      const data = await response.json()

      if (response.ok && data.token && data.user) {
        finalizeLogin(data)
      } else if (response.status === 202 && data.requiresTelegramConfirmation && data.challengeId) {
        setPendingConfirmation(true)
        pollLoginStatus(data.challengeId)
      } else {
        setError(data.error || 'Ошибка входа')
        setLoading(false)
      }
    } catch (err) {
      setError('Ошибка подключения к серверу')
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100">
            <span className="text-2xl">🎾</span>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Вход в систему
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            ERP Tennis - Система финансового учета
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">
                Email адрес
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Email адрес"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Пароль
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Пароль"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          {pendingConfirmation && (
            <div className="rounded-md bg-blue-50 p-4">
              <div className="text-sm text-blue-700">
                Подтвердите вход в Telegram. После нажатия кнопки `Подтвердить` вход выполнится автоматически.
              </div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading || pendingConfirmation}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {pendingConfirmation ? 'Ожидание подтверждения...' : loading ? 'Вход...' : 'Войти'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default Login


